import { createFactory, invoke } from '@withease/factories'
import { createEffect, createEvent, createStore, sample, UnitValue } from 'effector'
import { delay } from 'patronum'
import { createGate } from 'effector-react'
import { ErrorDescription } from '@shared/types'
import { EIdentityClientStep } from '../types'
import { createIdentityClientFormFactory } from './identity-client-form-factory'
import { checkSmsCodeQuery, sendSmsCodeQuery } from './api'

const PHONE_OR_CODE_IS_NOT_FOUND = 11
const WRONG_CODE = 13
const CODE_TIMEOUT = 14
const CODE_EXPIRE = 15

export type IdentityClientSubmit = (identityClientValues: {
  values: UnitValue<typeof identityClientForm.$values>
  agreements: unknown
}) => void

export const identityClientGate = createGate<{
  clientId: string
  platform: string
  referenceId: string
  lsKey: string
  onSubmit: IdentityClientSubmit
  onFail: () => void
}>()

const getErrorMessage = (errorCode?: number): string => {
  switch (errorCode) {
    case WRONG_CODE:
    case PHONE_OR_CODE_IS_NOT_FOUND:
      return 'Неправильный код'
    case CODE_TIMEOUT:
    case CODE_EXPIRE:
      return 'Время действия кода истекло'
    default:
      return 'Что-то пошло не так'
  }
}

export const createIdentityClientFactory = createFactory(() => {
  const $substep = createStore(EIdentityClientStep.IDENTITY_FORM)
  const $errorMessage = createStore('')

  const { identityClientForm, stores } = invoke(createIdentityClientFormFactory)

  const onMountFactoryEvent = createEvent()

  const onNextToSmsConfirmationEvent = createEvent()
  const onGoToChangePhoneStepEvent = createEvent()

  const sendSmsCodeEvent = createEvent()
  const checkSmsCodeEvent = createEvent<string>()

  sample({
    // TODO: EventCallable => Event
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    clock: onNextToSmsConfirmationEvent,
    target: identityClientForm.validate,
  })

  sample({
    clock: onNextToSmsConfirmationEvent,
    source: identityClientForm.$isValid,
    filter: (isValid) => isValid,
    fn: () => EIdentityClientStep.SMS_CONFIRMATION,
    target: $substep,
  })

  sample({
    clock: onGoToChangePhoneStepEvent,
    fn: () => EIdentityClientStep.IDENTITY_FORM,
    target: $substep,
  })

  sample({
    clock: sendSmsCodeEvent,
    source: {
      phone: identityClientForm.fields.phoneNumber.$value.map((phone) => phone.replace(/[^+0-9]/g, '')),
      clientId: identityClientGate.state.map(({ clientId }) => clientId),
    },
    fn: ({ phone, clientId }) => ({
      phone,
      clientId,
    }),
    target: sendSmsCodeQuery.start,
  })

  sample({
    clock: checkSmsCodeEvent,
    source: {
      phone: identityClientForm.fields.phoneNumber.$value.map((phone) => phone.replace(/[^+0-9]/g, '')),
      clientId: identityClientGate.state.map(({ clientId }) => clientId),
    },
    fn: ({ phone, clientId }, code) => ({
      phone,
      clientId,
      code,
    }),
    target: checkSmsCodeQuery.start,
  })

  sample({
    clock: checkSmsCodeQuery.finished.failure,
    fn: ({ error }) => getErrorMessage((error as ErrorDescription).response.meta.error),
    target: $errorMessage,
  })

  $errorMessage.reset(checkSmsCodeQuery.reset)

  delay({ source: checkSmsCodeQuery.finished.failure, timeout: 3000, target: checkSmsCodeQuery.reset })

  const onSubmitFx = createEffect(({ onSubmit, values }) => onSubmit(values))
  const onFailFx = createEffect(({ onFail }) => onFail())

  const $sendSmsCodeQuerySuccessData = createStore(null)

  sample({
    clock: sendSmsCodeQuery.finished.success,
    target: $sendSmsCodeQuerySuccessData,
  })

  sample({
    clock: checkSmsCodeQuery.finished.failure,
    source: {
      onFail: identityClientGate.state.map(({ onFail }) => onFail),
    },
    target: onFailFx,
  })

  sample({
    clock: checkSmsCodeQuery.finished.success,
    source: {
      onSubmit: identityClientGate.state.map(({ onSubmit }) => onSubmit),
      identityFormValues: identityClientForm.$values,
      sendData: $sendSmsCodeQuerySuccessData,
    },
    fn: (
      { onSubmit, identityFormValues, sendData },
      {
        result: {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          data: { access_token, date },
        },
        params,
      }
    ) => ({
      onSubmit,
      values: {
        access_token,
        smsCodeDateSend: sendData.result.data.date,
        smsCodeDateCheck: date,
        smsCode: params.code,
        ...identityFormValues,
      },
    }),
    target: onSubmitFx,
  })

  return {
    identityClientForm,

    stores: {
      $substep,
      ...stores,

      $pending: checkSmsCodeQuery.$pending,
      $failed: checkSmsCodeQuery.$failed,
      $errorMessage,
    },

    events: {
      onMountFactoryEvent,
      onNextToSmsConfirmationEvent,
      onGoToChangePhoneStepEvent,
      sendSmsCodeEvent,
      checkSmsCodeEvent,
    },
  }
})

export const { identityClientForm, stores, events } = invoke(createIdentityClientFactory)
